// F-098, F-100, F-101 : baux commerciaux et professionnels (liste, création, fiche, calendrier) et F-099 facturation.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post } from "@core/api";
import { date, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { ButtonLink, Button, Card, Empty, ErrorBox, Input, Loading, MoneyInput, Notice, PageHeader, Select, Table, Tabs, fieldError } from "@core/ui";
import { InvoiceTable, LeaseTermsPanel, TermsFields, termsFromApi, termsToBody, type TermsState } from "./conditions";
import { EVENT_KINDS, LEASE_STATUS, REGIMES, RuleNotice, Status, usePropertyOptions, useRun } from "./shared";

type Tab = "baux" | "calendrier";

export function CommercialLeasesPage() {
  const org = useOrg();
  const [tab, setTab] = useState<Tab>("baux");
  const q = useGet<any>(org.api("/pro/commercial-leases"));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Baux commerciaux et professionnels"
        intro="Deux régimes distincts de l’habitation : le bail commercial (9 ans, faculté triennale, indexation ILC ou ILAT) et le bail professionnel (6 ans, congé du preneur à tout moment avec 6 mois de préavis)."
        actions={org.can("operate") ? <ButtonLink to={org.to("professionnels/baux-commerciaux/nouveau")}>Nouveau bail</ButtonLink> : null}
      />
      <RuleNotice />
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        items={[
          { value: "baux", label: "Baux" },
          { value: "calendrier", label: "Échéances à venir" },
        ]}
      />
      {tab === "baux" ? (
        <Card>
          {q.isLoading ? <Loading /> : null}
          {q.data && !q.data.items.length ? <Empty title="Aucun bail commercial ou professionnel">Créez le bail depuis ce module : il ne reprend pas un modèle d’habitation.</Empty> : null}
          {q.data?.items.length ? (
            <Table caption="Baux commerciaux et professionnels">
              <thead>
                <tr>
                  <th>Locaux</th>
                  <th>Preneur</th>
                  <th>Régime</th>
                  <th>Statut</th>
                  <th className="num">Loyer HT mensuel</th>
                  <th>Prochaine échéance</th>
                  <th>Modèle</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((l: any) => (
                  <tr key={l.id}>
                    <td>
                      <Link to={org.to(`professionnels/baux-commerciaux/${l.id}`)}>{l.property_label}</Link>
                      {l.owner_name ? <span className="block muted small">{l.owner_name}</span> : null}
                    </td>
                    <td>{l.tenant_company ?? <span className="muted">Conditions à saisir</span>}</td>
                    <td>{REGIMES[l.regime]}</td>
                    <td>{LEASE_STATUS[l.status] ?? l.status}</td>
                    <td className="num">{money(l.rent_cents)}</td>
                    <td>{l.next_event_on ? `${date(l.next_event_on)} · ${l.next_event_label}` : "—"}</td>
                    <td>{l.legal_review_status ? <Status kind="review" value={l.legal_review_status} /> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Card>
      ) : (
        <Calendar />
      )}
    </div>
  );
}

function Calendar() {
  const org = useOrg();
  const [days, setDays] = useState("365");
  const q = useGet<any>(org.api(`/pro/commercial-calendar?days=${days}`));
  return (
    <Card title="Échéances enregistrées">
      <Select
        label="Horizon"
        value={days}
        options={[
          { value: "90", label: "3 mois" },
          { value: "180", label: "6 mois" },
          { value: "365", label: "1 an" },
          { value: "1095", label: "3 ans" },
        ]}
        onChange={(e) => setDays(e.target.value)}
      />
      {q.isLoading ? <Loading /> : null}
      {q.data && !q.data.items.length ? <Empty title="Aucune échéance enregistrée">Ouvrez un bail et enregistrez ses échéances pour recevoir des rappels.</Empty> : null}
      {q.data?.items.length ? (
        <Table caption="Échéances à venir">
          <thead>
            <tr>
              <th>Date</th>
              <th>Échéance</th>
              <th>Bail</th>
              <th>Rappel attribué à</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((e: any) => (
              <tr key={e.id}>
                <td>
                  {date(e.due_on)}
                  {e.overdue ? <Status kind="expense" value="expiree" /> : null}
                </td>
                <td>
                  <strong className="block">{EVENT_KINDS[e.kind] ?? e.kind}</strong>
                  <span className="small">{e.label}</span>
                </td>
                <td>
                  <Link to={org.to(`professionnels/baux-commerciaux/${e.lease_id}`)}>{e.property_label}</Link>
                  {e.tenant_company ? <span className="block muted small">{e.tenant_company}</span> : null}
                </td>
                <td>{e.assignee_name ?? "Personne"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </Card>
  );
}

export function CommercialLeasePage() {
  const { leaseId = "" } = useParams();
  const org = useOrg();
  return (
    <div className="page">
      <PageHeader eyebrow="Baux commerciaux et professionnels" title="Bail et conditions" actions={<ButtonLink variant="discret" to={org.to("professionnels/baux-commerciaux")}>Tous les baux</ButtonLink>} />
      <LeaseTermsPanel orgId={org.orgId} leaseId={leaseId} />
    </div>
  );
}

export function NewCommercialLeasePage() {
  const org = useOrg();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { options: properties, items } = usePropertyOptions(org.orgId);
  const { busy, error, run } = useRun();
  const [regime, setRegime] = useState("commercial");
  const [lease, setLease] = useState({ property_id: "", start_on: "", payment_day: "1", tenant_email: "" });
  const [rent, setRent] = useState<number | null>(null);
  const [charges, setCharges] = useState<number | null>(0);
  const [deposit, setDeposit] = useState<number | null>(0);
  const [terms, setTerms] = useState<TermsState>(() => termsFromApi(null, [], "commercial"));
  const defaults = useGet<any>(org.api("/pro/commercial-leases"));
  useEffect(() => {
    const inv = defaults.data?.default_inventory;
    if (inv) setTerms((t) => (t.charges_inventory.length ? t : { ...t, charges_inventory: inv }));
  }, [defaults.data]);
  const property = items.find((p) => p.id === lease.property_id);
  return (
    <div className="page">
      <PageHeader eyebrow="Baux commerciaux et professionnels" title="Nouveau bail" intro="Le bail est créé en brouillon avec ses conditions. Faites valider le projet par un professionnel du droit avant de le faire signer." />
      <RuleNotice />
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const body = {
            ...termsToBody(terms),
            charges_inventory: terms.charges_inventory.length ? terms.charges_inventory : null,
            property_id: lease.property_id,
            regime,
            start_on: lease.start_on,
            payment_day: parseInt(lease.payment_day, 10) || 1,
            tenant_email: lease.tenant_email || null,
            rent_cents: rent ?? 0,
            charges_cents: charges ?? 0,
            deposit_cents: deposit ?? 0,
          };
          const r: any = await run(() => post(org.api("/pro/commercial-leases"), body), "Bail créé en brouillon.");
          if (r) {
            invalidate(org.api("/pro"));
            navigate(org.to(`professionnels/baux-commerciaux/${r.lease.id}`));
          }
        }}
      >
        <Card title="Locaux et régime">
          <div className="form-row">
            <Select
              label="Locaux"
              value={lease.property_id}
              options={properties}
              help="Le régime dépend du contrat et de l’activité, pas du seul type de local."
              onChange={(e) => setLease({ ...lease, property_id: e.target.value })}
            />
            <Select
              label="Régime"
              value={regime}
              options={[
                { value: "commercial", label: "Bail commercial (3-6-9)" },
                { value: "professionnel", label: "Bail professionnel" },
              ]}
              onChange={(e) => {
                setRegime(e.target.value);
                setTerms({ ...terms, duration_months: e.target.value === "professionnel" ? "72" : "108", triennial_termination: e.target.value !== "professionnel" });
              }}
            />
          </div>
          {property && !["local_commercial", "local_professionnel"].includes(property.kind) ? (
            <Notice tone="attention">Ce local est enregistré comme « {property.kind} ». Vérifiez que le régime choisi correspond bien au contrat.</Notice>
          ) : null}
          <div className="form-row">
            <Input label="Date d’effet" type="date" required value={lease.start_on} onChange={(e) => setLease({ ...lease, start_on: e.target.value })} />
            <MoneyInput label="Loyer mensuel hors taxes et hors charges" cents={rent} onChange={setRent} error={fieldError(error, "rent_cents")} required />
            <MoneyInput label="Provision sur charges mensuelle" cents={charges} onChange={setCharges} />
            <MoneyInput label="Dépôt de garantie" cents={deposit} onChange={setDeposit} />
          </div>
          <div className="form-row">
            <Input label="Jour de paiement" inputMode="numeric" value={lease.payment_day} error={fieldError(error, "payment_day")} onChange={(e) => setLease({ ...lease, payment_day: e.target.value })} />
            <Input label="E-mail du preneur" type="email" value={lease.tenant_email} error={fieldError(error, "tenant_email")} onChange={(e) => setLease({ ...lease, tenant_email: e.target.value })} />
          </div>
        </Card>
        <Card title="Conditions">
          <TermsFields regime={regime} value={terms} onChange={setTerms} error={error} />
        </Card>
        <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
        {error && (error as any).fields?.length ? <Notice tone="erreur">Certains champs sont à corriger : voir les messages ci-dessus.</Notice> : null}
        <div>
          <Button type="submit" busy={busy} disabled={!lease.property_id || !lease.start_on}>
            Créer le bail en brouillon
          </Button>
        </div>
      </form>
    </div>
  );
}

export function BillingPage() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/invoices"));
  const leases = useGet<any>(org.api("/pro/commercial-leases"));
  const invalidate = useInvalidate();
  const [leaseId, setLeaseId] = useState("");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Facturation des loyers commerciaux"
        intro="Factures et avoirs des baux commerciaux et professionnels, numérotés dans une suite continue propre à l’espace. Une facture corrigée ne se supprime pas : elle est annulée par un avoir."
      />
      <Notice tone="attention" title="Points à valider (D-21)">
        La TVA n’est appliquée que si l’option du bailleur est enregistrée dans les conditions du bail. {q.data?.e_invoicing_note}
      </Notice>
      <Notice>Une facture n’est pas une quittance : pour les baux d’habitation, utilisez les quittances et reçus de la gestion des loyers.</Notice>
      <Card title="Factures et avoirs">
        {q.data?.next_number ? <p className="small muted">Prochain numéro : {q.data.next_number}</p> : null}
        {q.isLoading ? <Loading /> : null}
        {q.data?.items.length ? <InvoiceTable items={q.data.items} orgId={org.orgId} onChange={() => invalidate(org.api("/pro/invoices"))} /> : <Empty title="Aucune facture">Émettez les factures depuis la fiche de chaque bail commercial.</Empty>}
      </Card>
      {org.can("finance") ? (
        <Card title="Émettre une facture">
          <Select
            label="Bail"
            value={leaseId}
            options={[{ value: "", label: "Choisir un bail commercial ou professionnel" }, ...(leases.data?.items ?? []).map((l: any) => ({ value: l.id, label: `${l.property_label}${l.tenant_company ? ` · ${l.tenant_company}` : ""}` }))]}
            onChange={(e) => setLeaseId(e.target.value)}
          />
          {leaseId ? (
            <p>
              <Link to={org.to(`professionnels/baux-commerciaux/${leaseId}`)}>Ouvrir la fiche du bail</Link>, onglet « Factures », pour choisir la période et émettre la facture.
            </p>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
